import { Check, Pipette } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { cn } from '@/lib/utils';

const ACE_ACCENT_PRESETS = [
  { label: 'Índigo', hex: '#6366f1' },
  { label: 'Amarelo', hex: '#eab308' },
  { label: 'Rosa', hex: '#f43f5e' },
  { label: 'Céu', hex: '#38bdf8' },
  { label: 'Roxo', hex: '#a855f7' },
  { label: 'Pink', hex: '#ec4899' },
  { label: 'Âmbar', hex: '#f59e0b' },
  { label: 'Esmeralda', hex: '#10b981' },
  { label: 'Ciano', hex: '#06b6d4' },
  { label: 'Violeta', hex: '#8b5cf6' },
  { label: 'Laranja', hex: '#f97316' },
  { label: 'Lima', hex: '#84cc16' },
] as const;

function normalizeHex(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const [, r, g, b] = withHash;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return raw;
}

type AceAccentColorPickerProps = {
  value: string;
  onChange: (hex: string) => void;
};

export function AceAccentColorPicker({ value, onChange }: AceAccentColorPickerProps) {
  const selected = normalizeHex(value) || '#6366f1';
  const colorInputValue = /^#[0-9a-fA-F]{6}$/.test(selected) ? selected : '#6366f1';

  return (
    <div className="space-y-4 rounded-xl border border-white/[0.08] bg-gradient-to-br from-white/[0.05] to-white/[0.02] p-4">
      <div className="flex items-center gap-4">
        <div
          className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ring-2 ring-white/15"
          style={{
            background: `radial-gradient(circle at 35% 30%, ${selected}88 0%, ${selected} 55%, ${selected}cc 100%)`,
            boxShadow: `0 0 28px ${selected}55, inset 0 1px 0 rgba(255,255,255,0.25)`,
          }}
        >
          <Pipette className="h-6 w-6 text-white/90 drop-shadow" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-poppins text-sm font-semibold text-white">Cor de destaque</p>
          <p className="mt-0.5 font-inter text-xs text-slate-400">
            Usada no cartão e detalhes do Ace no site público.
          </p>
          <p className="mt-2 font-mono text-xs font-medium uppercase tracking-wide text-sky-200/90">
            {selected}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-2 sm:grid-cols-6">
        {ACE_ACCENT_PRESETS.map((preset) => {
          const active = selected.toLowerCase() === preset.hex.toLowerCase();

          return (
            <button
              key={preset.hex}
              type="button"
              title={preset.label}
              aria-label={`${preset.label} (${preset.hex})`}
              aria-pressed={active}
              onClick={() => onChange(preset.hex)}
              className={cn(
                'group relative aspect-square rounded-xl border transition-all duration-200',
                'hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                active
                  ? 'border-white/50 ring-2 ring-white/30'
                  : 'border-white/10 hover:border-white/25'
              )}
              style={{
                backgroundColor: preset.hex,
                boxShadow: active ? `0 0 18px ${preset.hex}66` : undefined,
              }}
            >
              {active ? (
                <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-md" />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
        <div className="grid gap-2">
          <Label className="text-xs text-blue-100/80">Personalizada</Label>
          <label className="relative inline-flex h-10 w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-white/[0.04] sm:w-14">
            <input
              type="color"
              value={colorInputValue}
              onChange={(e) => onChange(e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label="Seletor de cor personalizada"
            />
            <span
              className="h-7 w-7 rounded-lg border border-white/20 shadow-inner"
              style={{ backgroundColor: colorInputValue }}
              aria-hidden
            />
          </label>
        </div>
        <div className="grid gap-2">
          <Label className="text-xs text-blue-100/80">Código hex</Label>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => {
              const next = normalizeHex(e.target.value);
              if (next && next !== value) onChange(next);
            }}
            placeholder="#6366f1"
            className={cn(painelInputClass, 'font-mono text-sm')}
          />
        </div>
      </div>
    </div>
  );
}
